/**
 * NavbarG.js - Navbar de Administración con Sistema de Permisos por AdminLevel
 * 
 * SISTEMA DE NIVELES:
 * ==================
 * AdminLevel 1 (Básico): 
 *   - Inicio
 *   - Empleados (gestión)
 *   - Reclamos (gestión)
 *   - Gestión (Beneficios, Talles, Resumen Capacitaciones)
 * 
 * AdminLevel 2 (Avanzado):
 *   - Todo lo de Nivel 1 +
 *   - Cargas (Capacitaciones, Diagramas, Recibos, Documentos)
 * 
 * AdminLevel 3 (Super Admin):
 *   - Todo lo de Nivel 1 y 2 +
 *   - Dashboard de Actividades (Logs, Estadísticas, KPIs)
 * 
 * La configuración se maneja en NAV_ITEMS con el campo minAdminLevel
 */

import React, { useState, useMemo } from 'react';
import { Navbar, Container, Nav, Offcanvas, Button, NavDropdown } from 'react-bootstrap';
import { FaSignOutAlt, FaHome, FaChartLine, FaUsers, FaExclamationTriangle, FaClipboardList, FaFileAlt } from 'react-icons/fa';
import Logo2 from '../../../assets/GP_logo_corto.png';
import hola from '../../../assets/hola.jpeg';
import { useNavigate, Link } from 'react-router-dom';
// import RoleSwitcher from '../../../components/RoleSwitcher';
// import VersionSwitcher from '../../../components/VersionSwitcher';
import '../styles/navbarG.css';

// Configuración de navegación con permisos
const NAV_ITEMS = [
  {
    id: 'inicio',
    label: 'Inicio',
    path: '/administrador',
    icon: FaHome,
    minAdminLevel: 1
  },
  {
    id: 'empleados',
    label: 'Empleados',
    path: '/administrador/administrar_usuarios',
    icon: FaUsers,
    minAdminLevel: 1,
    description: 'Gestión de empleados'
  },
  {
    id: 'reclamos',
    label: 'Reclamos',
    path: '/administrador/reclamos',
    icon: FaExclamationTriangle,
    minAdminLevel: 1,
    description: 'Gestión de reclamos'
  },
  {
    id: 'cargas',
    label: 'Cargas',
    icon: FaClipboardList,
    minAdminLevel: 2,
    description: 'Módulo de cargas (AdminLevel 2+)',
    isDropdown: true,
    children: [
      // {
      //   id: 'capacitaciones',
      //   label: 'Gestión Capacitaciones',
      //   path: '/administrador/capacitaciones',
      //   minAdminLevel: 2
      // },
      {
        id: 'cargar-capacitaciones',
        label: 'Capacitaciones (Viejo)',
        path: '/administrador/cargar-capacitaciones',
        minAdminLevel: 2
      },
      // {
      //   id: 'diagramas-modulo',
      //   label: 'Diagramas (Módulo Admin)',
      //   path: '/administrador/diagramas',
      //   minAdminLevel: 2
      // },
      // {
      //   id: 'diagramas-inspeccion',
      //   label: 'Diagramas de Inspección',
      //   path: '/administrador/diagramas-inspeccion',
      //   minAdminLevel: 2
      // },
      {
        id: 'recibos',
        label: 'Recibos',
        path: '/administrador/recibos',
        minAdminLevel: 2
      },
      {
        id: 'documentos',
        label: 'Documentos',
        path: '/administrador/documentos',
        minAdminLevel: 2
      }
      // {
      //   id: 'procedimientos',
      //   label: 'Procedimientos',
      //   path: '/home/admin/procedimientos',
      //   minAdminLevel: 2
      // }
    ]
  },
  {
    id: 'gestion',
    label: 'Gestión',
    icon: FaFileAlt,
    minAdminLevel: 1,
    isDropdown: true,
    children: [
      {
        id: 'resumen-capacitaciones',
        label: 'Resumen Capacitaciones',
        path: '/administrador/resumen-capacitaciones',
        minAdminLevel: 1
      },
      {
        id: 'beneficios',
        label: 'Beneficios',
        path: '/administrador/beneficios',
        minAdminLevel: 1
      },
      {
        id: 'talles',
        label: 'Talles',
        path: '/administrador/talles',
        minAdminLevel: 1
      }
    ]
  },
  {
    id: 'actividades',
    label: 'Actividades',
    icon: FaChartLine,
    minAdminLevel: 3,
    description: 'Dashboards de actividades (Solo Super Admin)',
    isDropdown: true,
    children: [
      {
        id: 'dashboard-actividades',
        label: 'Vista General',
        path: '/administrador/dashboard-actividades',
        minAdminLevel: 3
      },
      {
        id: 'dashboard-metricas',
        label: 'Métricas Avanzadas',
        path: '/administrador/dashboard-metricas',
        minAdminLevel: 3
      }
    ]
  }
];

function GerenciaNavbar() {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  // Obtener nivel de admin del usuario
  const adminLevel = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return Number(user.AdminLevel) || 1;
    } catch {
      return 1;
    }
  }, []);

  // Obtener nombre de usuario
  const userName = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.Nombre || 'Usuario';
    } catch {
      return 'Usuario';
    }
  }, []);

  // Obtener legajo/CdUsuario
  const userLegajo = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.CdUsuario || null;
    } catch {
      return null;
    }
  }, []);

  // Verificar si es el legajo 43
  const isLegajo43 = userLegajo === 43 || userLegajo === '43' || String(userLegajo) === '43';

  // Etiquetas de nivel
  const adminLevelLabel = {
    1: 'Básico',
    2: 'Avanzado',
    3: 'Super Admin'
  };

  const adminLevelColor = {
    1: 'warning',
    2: 'info',
    3: 'danger'
  };

  // Filtrar items según nivel de admin
  const visibleItems = useMemo(() => {
    return NAV_ITEMS.filter(item => item.minAdminLevel <= adminLevel).map(item => {
      if (item.isDropdown && item.children) {
        return {
          ...item,
          children: item.children.filter(child => child.minAdminLevel <= adminLevel)
        };
      }
      return item;
    }).filter(item => !item.isDropdown || (item.children && item.children.length > 0));
  }, [adminLevel]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user_permisos');
    localStorage.removeItem('user_permisos_timestamp');
    navigate('/');
  };

  const renderNavItem = (item, isMobile = false) => {
    const Icon = item.icon;
    
    if (item.isDropdown) {
      return (
        <NavDropdown 
          key={item.id}
          title={
            <span>
              {Icon && <Icon className="me-2" />}
              {item.label}
            </span>
          }
          id={`${item.id}-dropdown${isMobile ? '-mobile' : ''}`}
          className="nav-link"
        >
          {item.children.map(child => (
            <NavDropdown.Item 
              key={child.id}
              as={Link} 
              to={child.path}
              onClick={isMobile ? () => setShowMenu(false) : undefined}
            >
              {child.label}
            </NavDropdown.Item>
          ))}
        </NavDropdown>
      );
    }

    return (
      <Nav.Link 
        key={item.id}
        as={Link} 
        to={item.path} 
        className="nav-link"
        onClick={isMobile ? () => setShowMenu(false) : undefined}
        title={item.description}
      >
        {Icon && <Icon className="me-2 d-inline d-lg-none" />}
        {item.label}
      </Nav.Link>
    );
  };

  return (
    <Navbar expand="lg" className="gerencia-navbar">
      <Container fluid>

        <Navbar.Brand as={Link} to="/administrador" className="mx-auto mx-lg-0">
          <img 
            src={Logo2}
            alt="Grupo Sarmiento Logo"
            className="navbar-logo" 
            onError={(e) => e.target.src = Logo2} 
          />
        </Navbar.Brand>

        <Navbar.Toggle 
          aria-controls="offcanvasNavbar" 
          className="d-lg-none hamburger-btn"
          onClick={() => setShowMenu(true)}
        />

        {/* Desktop Menu */}
        <Navbar.Collapse id="basic-navbar-nav" className="d-none d-lg-block">
          <Nav className="ms-auto align-items-center">
            {visibleItems.map(item => renderNavItem(item, false))}

            {/* Badge de nivel de admin */}
            <span className={`badge bg-${adminLevelColor[adminLevel]} ms-3 admin-level-badge`}>
              Nivel {adminLevel}: {adminLevelLabel[adminLevel]}
            </span>

            {/* Switch de rol Admin/Empleado */}
            {/* <RoleSwitcher /> */}
            
            {/* Cambiar a v2.0 */}
            {/* <VersionSwitcher /> */}

            {/* Avatar especial para legajo 43 */}
            {isLegajo43 && (
              <img 
                src={hola} 
                alt="Avatar" 
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  marginLeft: '15px',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              />
            )}

            <Button 
              variant="link" 
              className="logout-btn"
              onClick={handleLogout}
              title="Cerrar sesión"
            >
              <FaSignOutAlt className="me-2 logout-n" />
            </Button>
          </Nav>
        </Navbar.Collapse>

        {/* Mobile Menu */}
        <Offcanvas 
          show={showMenu} 
          onHide={() => setShowMenu(false)} 
          placement="end" 
          className="gerencia-offcanvas"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              {/* Avatar especial para legajo 43 en mobile */}
              {isLegajo43 && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <img 
                    src={hola} 
                    alt="Avatar" 
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginRight: '10px',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                  />
                  <span>{userName}</span>
                </div>
              )}
              {!isLegajo43 && 'Menú'}
              <div className="mt-2">
                <span className={`badge bg-${adminLevelColor[adminLevel]} admin-level-badge-mobile`}>
                  Nivel {adminLevel}: {adminLevelLabel[adminLevel]}
                </span>
              </div>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Nav className="flex-column">
              {visibleItems.map(item => renderNavItem(item, true))}

              <div className="mt-3 pt-3 border-top">
                <Button 
                  variant="outline-light" 
                  className="w-100 logout-btn-mobile"
                  onClick={() => {
                    handleLogout();
                    setShowMenu(false);
                  }}
                >
                  <FaSignOutAlt className="me-2" />
                  Cerrar sesión
                </Button>
              </div>
            </Nav>
          </Offcanvas.Body>
        </Offcanvas>
      </Container>
    </Navbar>
  );
}

export default GerenciaNavbar;
